import React, { PropsWithChildren, useMemo, useRef } from 'react';
import { SupplierItemProps, SupplierTableProps } from './type';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, TableBody } from '@leadsnavi/ui-kit';
import { Checkbox, TableCell, TableHead, TableHeader, TableRow } from '@leadsnavi/ui-kit';
import { Table } from '@leadsnavi/ui-kit';
import { useMemoizedFn } from 'ahooks';
import { formatDollarAmount } from '@/utils/number';
import { FixedHeaderTable } from '../base/fixed-header-table';
import { ChevronUp } from 'lucide-react';
import { EmptyPlaceholder } from '@/common/base/empty-placeholder';
import { CountryFlag } from '@/common/base/country-flag';
import cn from 'classnames';
import { Ellipse } from '@/common/base/ellipse';
import SparlesLightIcon from '@/assets/icons/sparles-light.svg?react';
import { Skeleton } from '@/common/base/skeleton';
import { columnWidth, SkeletonItem } from './skeleton-item';
import { formatDateString } from '@/utils/dayjs';
import mixpanel from 'mixpanel-browser';
import { TextTranslate } from '@/components/translate-icon';
import { MatchBadge } from '../base/match-badge';
import { highlightText } from '@/utils/string';
import { StatusTag } from '@/common/biz/status-tag';
import { CompanyLogo } from './components/company-logo';

const hCellClass = 'text-[color:var(--600-fill-,#525252)] [font-family:Geist] text-sm font-normal';

const CheckBoxSpace: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  return <div className="inline-flex items-center  self-center  w-9  shrink-0">{children}</div>;
};
const SupplierItem: React.FC<SupplierItemProps> = props => {
  const { supplier, isSelected, isAiSummaryLoding, isKeywordLoading, onSelectChange, onDetailClick } = props;
  const { recommendReason } = supplier;
  const [isHover, setIsHover] = React.useState(false);
  // const textV2 = useMemo(() => {
  //   return highlightText(truncateText(supplier.highLight?.value || ''));
  // }, [supplier.highLight?.value]);

  const matchs = useMemo(() => {
    if (!recommendReason) return [];
    return recommendReason.split('、').map(item => {
      return {
        label: item,
        checked: true,
      };
    });
  }, [recommendReason]);

  const renderCell = (value: React.ReactNode) => {
    if (isKeywordLoading) {
      return <Skeleton className="h-4 w-full rounded-[2px]" />;
    }
    return value;
  };
  return (
    <React.Fragment key={supplier.companyId}>
      <TableRow
        key={supplier.companyId}
        className={`border-none text-[#262626] cursor-pointer ${isHover ? 'bg-[#FAFAFA]' : ''}`}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={() => onDetailClick?.(supplier)}
      >
        <TableCell className={cn(columnWidth[0], 'pt-4 font-medium overflow-hidden')}>
          <div className="flex items-center overflow-hidden">
            <CheckBoxSpace>
              <Checkbox
                checked={isSelected}
                id="search_company_select_click"
                onClick={e => {
                  e.stopPropagation();
                  onSelectChange?.();
                  mixpanel.track('search_company_select_click');
                }}
              />
            </CheckBoxSpace>

            <div className=" flex items-center overflow-hidden gap-1">
              <CompanyLogo imageUrl={supplier.logo} />
              <span className=" cursor-pointer overflow-hidden text-base text-ellipsis whitespace-nowrap">
                {supplier.name}
              </span>
              <div className=" shrink-0">
                <CountryFlag countryCode={supplier.standardCountry} />
              </div>
              {supplier.send ? <StatusTag showDot={false} text="Sent" status={'primary'} /> : null}
            </div>
          </div>
        </TableCell>
        <TableCell className={cn(columnWidth[1], 'pt-4')}>
          {renderCell(supplier.allTotalTransactions || '--')}
        </TableCell>
        <TableCell className={cn(columnWidth[2], 'pt-4')}>
          {renderCell(supplier.allValueOfGoodsUSDDouble ? formatDollarAmount(supplier.allValueOfGoodsUSDDouble) : '--')}
        </TableCell>
        <TableCell className={cn(columnWidth[3], 'pt-4 text-left')}>
          {renderCell(formatDateString(supplier.allLastTransactionDate) || '--')}
        </TableCell>
      </TableRow>

      {/* 跨行的 row */}
      <TableRow
        className={` border-b-[1px] cursor-pointer ${isHover ? 'bg-[#FAFAFA]' : ''}`}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onClick={() => onDetailClick?.(supplier)}
      >
        <TableCell colSpan={4} className=" pt-0 px-4 pb-4">
          <div className=" flex  items-start ">
            <CheckBoxSpace></CheckBoxSpace>
            <div className="w-full overflow-hidden">
              {/* 跨行的内容需要放到这里 */}
              {/* 命中的关键词 */}
              {/* AI 生成的简单介绍 */}
              {!isKeywordLoading && supplier.highLight?.value ? (
                <div className="mb-2  h-5 max-w-[38%]">
                  <TextTranslate
                    text={supplier.highLight?.value}
                    renderText={text => {
                      const truncatedText = highlightText(text);
                      return (
                        <Ellipse
                          text={truncatedText}
                          tooltipText={truncatedText}
                          className=" text-[color:var(--800-fill-,#A3A3A3)] text-ellipsis text-xs"
                        ></Ellipse>
                      );
                    }}
                  />
                </div>
              ) : null}
              {isKeywordLoading ? <Skeleton className="size-[14px]  rounded-[2px] w-[66%] shrink-0 mb-2" /> : null}

              {!isAiSummaryLoding && supplier.refinedSellingPoint ? (
                <div className="flex items-center gap-2">
                  {matchs.length > 0 && <MatchBadge text="Matches" matches={matchs} />}
                  <TextTranslate
                    text={supplier.refinedSellingPoint}
                    renderText={text => {
                      return (
                        <div className=" overflow-hidden max-w-full  inline-flex h-[22px] items-center  gap-1 border [background:var(--100-fill-,#F5F5F5)] px-2 py-0.5 rounded-[28px] border-solid border-[rgba(0,0,0,0.00)]">
                          <SparlesLightIcon />
                          <Ellipse
                            text={text}
                            className=" text-[color:var(--800-fill-,#262626)] text-ellipsis text-xs"
                          ></Ellipse>
                        </div>
                      );
                    }}
                  />
                </div>
              ) : null}

              {isAiSummaryLoding ? <Skeleton className="size-[14px]  rounded-[2px] w-[40%] shrink-0" /> : null}
            </div>
          </div>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

type CheckBoxValue = boolean | 'indeterminate';

export const SupplierTable: React.FC<SupplierTableProps & { useFixedHeader?: boolean }> = props => {
  const {
    suppliers = [],
    showSkeleton,
    selectFilter,
    onSelectChange,
    onSelectAllClick,
    onDetailClick,
    useFixedHeader = false,
    isAiSummaryLoding,
    isKeywordLoading,
  } = props;
  // const showSkeleton = true;
  // const suppliers = [];
  const [open, setOpen] = React.useState(false);

  const selectedIdsSet = useMemo(() => {
    return new Set(selectFilter.includeIds || []);
  }, [selectFilter.includeIds]);
  const onSelectCurrentPageClick = useMemoizedFn(() => {
    const listIds = suppliers.map(supplier => supplier.companyId);
    onSelectChange?.({
      includeIds: listIds,
    });
  });
  const onItemSelectToggle = useMemoizedFn((companyId: string) => {
    const isSelected = selectedIdsSet.has(companyId);
    if (isSelected) {
      selectedIdsSet.delete(companyId);
    } else {
      selectedIdsSet.add(companyId);
    }
    onSelectChange?.({
      includeIds: [...selectedIdsSet],
    });
  });
  const onHeadSelectToggle = useMemoizedFn(() => {
    const isSelected = currentPageSelectedValue === true;
    const currentPageIdList = suppliers.map(supplier => supplier.companyId);

    if (isSelected) {
      // 从当前页选择的供应商中，移除当前页的所有供应商
      onSelectChange?.({
        includeIds: selectFilter.includeIds?.filter(item => !currentPageIdList.includes(item)) || [],
      });
    } else {
      onSelectChange?.({
        includeIds: [...new Set([...selectedIdsSet, ...currentPageIdList])],
      });
    }
  });
  const onUnselectAllClick = useMemoizedFn(() => {
    onSelectChange?.({
      includeIds: [],
    });
  });

  const currentPageSelectedValue = useMemo<CheckBoxValue>(() => {
    const idSet = new Set(suppliers.map(supplier => supplier.companyId));
    const list = selectFilter.includeIds?.filter(item => {
      return idSet.has(item);
    });
    if (list?.length === suppliers.length) {
      return true;
    }
    if (list?.length === 0) {
      return false;
    }
    return 'indeterminate';
  }, [selectFilter.includeIds, suppliers]);
  const closeTimerRef = useRef<unknown>(null);
  const delayCloseDropMenu = useMemoizedFn(() => {
    closeTimerRef.current = setTimeout(() => {
      clearTimeout(closeTimerRef.current as number);
      setOpen(false);
    }, 300);
  });

  const headerSelectEl = (
    <CheckBoxSpace>
      <Checkbox
        checked={currentPageSelectedValue}
        id="search_company_select_click"
        onClick={() => {
          mixpanel.track('search_company_select_click');
          mixpanel.track('select_all_click');
          onHeadSelectToggle();
        }}
      />
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          onMouseEnter={() => {
            clearTimeout(closeTimerRef.current as number);
            setOpen(true);
          }}
          onMouseLeave={() => {
            delayCloseDropMenu();
          }}
        >
          <div className="h-full px-0.5">
            <ChevronUp size={12} className=" rotate-180 cursor-pointer" />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="bottom"
          align="start"
          alignOffset={-20}
          onMouseEnter={() => {
            clearTimeout(closeTimerRef.current as number);
            setOpen(true);
          }}
          onMouseLeave={() => {
            delayCloseDropMenu();
          }}
        >
          <DropdownMenuItem
            className=" cursor-pointer"
            id="search_company_select_click"
            onClick={() => {
              mixpanel.track('search_company_select_click');
              mixpanel.track('select_all_click');
              onSelectCurrentPageClick();
            }}
          >
            Select This Page
          </DropdownMenuItem>
          <DropdownMenuItem
            className=" cursor-pointer  "
            id="search_company_select_click"
            onClick={() => {
              mixpanel.track('search_company_select_click');
              mixpanel.track('select_all_click');
              onSelectAllClick();
            }}
          >
            {/* 选择当前页及以后1000条数据 翻译成英文*/}
            Select This Page and Next 1000
          </DropdownMenuItem>
          <DropdownMenuItem
            className=" cursor-pointer"
            id="search_company_select_click"
            onClick={() => {
              mixpanel.track('search_company_select_click');
              mixpanel.track('select_all_click');
              onUnselectAllClick();
            }}
          >
            {/* 取消全部选择 翻译成英文*/}
            Unselect All
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </CheckBoxSpace>
  );
  return (
    <React.Fragment>
      {useFixedHeader ? (
        <FixedHeaderTable
          header={
            <TableHeader>
              <TableRow className="bg-[#FAFAFA] border-none ">
                <TableHead className={cn(columnWidth[0], hCellClass)}>
                  <div className="flex items-center">{headerSelectEl}Supplier Name</div>
                </TableHead>
                <TableHead className={cn(columnWidth[1], hCellClass)}>Exports Count</TableHead>
                <TableHead className={cn(columnWidth[2], hCellClass)}>Exports Value</TableHead>
                <TableHead className={cn(columnWidth[3], hCellClass)}>Last Export</TableHead>
              </TableRow>
            </TableHeader>
          }
        >
          {suppliers.length === 0 && !showSkeleton ? (
            <td>
              <EmptyPlaceholder description="There are no search results for the moment" className="py-[20vh]" />
            </td>
          ) : null}
          {suppliers.length === 0 && showSkeleton ? (
            <React.Fragment>
              <td>
                <SkeletonItem />
                <SkeletonItem />
                <SkeletonItem />
                <SkeletonItem />
                <SkeletonItem />
              </td>
            </React.Fragment>
          ) : null}
          {suppliers.map(supplier => {
            const isSelected = selectedIdsSet.has(supplier.companyId);
            return (
              <SupplierItem
                key={supplier.companyId}
                supplier={supplier}
                isSelected={isSelected}
                isAiSummaryLoding={isAiSummaryLoding}
                isKeywordLoading={isKeywordLoading}
                onSelectChange={() => {
                  onItemSelectToggle(supplier.companyId);
                }}
                onDetailClick={() => {
                  onDetailClick(supplier);
                }}
              />
            );
          })}
        </FixedHeaderTable>
      ) : (
        <React.Fragment>
          {/*  原始表格结构 */}
          <Table className="border-none table-fixed overflow-x-hidden">
            <TableHeader>
              <TableRow className="bg-[#FAFAFA] border-none ">
                <TableHead className={cn(columnWidth[0], hCellClass)}>
                  <div className="flex items-center">{headerSelectEl}Supplier Name</div>
                </TableHead>
                <TableHead className={cn(columnWidth[1], hCellClass)}>Exports Count</TableHead>
                <TableHead className={cn(columnWidth[2], hCellClass)}>Exports Value</TableHead>
                <TableHead className={cn(columnWidth[3], hCellClass)}>Last Export</TableHead>
              </TableRow>
            </TableHeader>

            {/* 表格内容 */}
            <TableBody>
              {suppliers.map(supplier => {
                const isSelected = selectedIdsSet.has(supplier.companyId);

                return (
                  <SupplierItem
                    key={supplier.companyId}
                    supplier={supplier}
                    isSelected={isSelected}
                    isAiSummaryLoding={isAiSummaryLoding}
                    isKeywordLoading={isKeywordLoading}
                    onSelectChange={() => onItemSelectToggle(supplier.companyId)}
                    onDetailClick={() => {
                      onDetailClick(supplier);
                    }}
                  />
                );
              })}
            </TableBody>
          </Table>
          {suppliers.length === 0 && !showSkeleton ? (
            <EmptyPlaceholder description="There are no search results for the moment" className="py-4" />
          ) : null}
          {suppliers.length === 0 && showSkeleton ? (
            <React.Fragment>
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
              <SkeletonItem />
            </React.Fragment>
          ) : null}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
